/**
 * Shapers Club — Flash Checkout Offer
 * Version 2.2.0 (SC V18 : tutoiement, titres courts pour le bloc compact)
 *
 * Fonctionnement :
 * - déclenche l’offre lorsque le panier atteint 300 € ;
 * - exige au moins un produit portant le tag "Surfboards" ;
 * - conserve le timer après actualisation et rafraîchissement AJAX ;
 * - ne relance pas le timer lorsqu’une nouvelle planche est ajoutée ;
 * - continue le timer lorsque la planche est retirée ;
 * - masque l’offre pendant 7 jours après expiration ;
 * - affiche le total estimé remisé dans le total natif du drawer ;
 * - applique le code SHAPERS5 au passage au checkout.
 */

(() => {
  'use strict';

  /* ==========================================================================
     Configuration
     ========================================================================== */

  const STORAGE_KEY = 'sc_flash_offer_v2';

  const OFFER_DURATION_MS = 60 * 60 * 1000;
  const COOLDOWN_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

  let updateInterval = null;
  let observerUpdateFrame = null;

  /* ==========================================================================
     Stockage local
     ========================================================================== */

  function getStoredState() {
    try {
      const rawState = localStorage.getItem(STORAGE_KEY);

      if (!rawState) {
        return null;
      }

      const parsedState = JSON.parse(rawState);

      if (
        typeof parsedState !== 'object' ||
        parsedState === null ||
        typeof parsedState.expiresAt !== 'number'
      ) {
        clearStoredState();
        return null;
      }

      return parsedState;
    } catch (error) {
      console.warn(
        '[Shapers Club] État de la Flash Offer invalide.',
        error
      );

      clearStoredState();
      return null;
    }
  }

  function saveStoredState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn(
        '[Shapers Club] Impossible d’enregistrer la Flash Offer.',
        error
      );
    }
  }

  function clearStoredState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn(
        '[Shapers Club] Impossible de supprimer la Flash Offer.',
        error
      );
    }
  }

  /* ==========================================================================
     Lecture du panier
     ========================================================================== */

  function parseBoolean(value) {
    return value === 'true';
  }

  function getOfferElement() {
    return document.querySelector('[data-sc-cart-flash-offer]');
  }

  function getCartData(element) {
    return {
      isEligible: parseBoolean(element.dataset.cartEligible),
      hasSurfboard: parseBoolean(element.dataset.hasSurfboard),
      cartTotal: Number(element.dataset.cartTotal || 0),
      threshold: Number(element.dataset.threshold || 30000),
      percentage: Number(element.dataset.percentage || 5),
      discountCode: element.dataset.discountCode || 'SHAPERS5'
    };
  }

  /* ==========================================================================
     Gestion de l’état de l’offre
     ========================================================================== */

  function startOffer(now = Date.now()) {
    const state = {
      startedAt: now,
      expiresAt: now + OFFER_DURATION_MS,
      cooldownUntil: null
    };

    saveStoredState(state);

    return state;
  }

  function expireOffer(state, now = Date.now()) {
    const expiredState = {
      startedAt:
        typeof state?.startedAt === 'number'
          ? state.startedAt
          : now - OFFER_DURATION_MS,

      expiresAt:
        typeof state?.expiresAt === 'number'
          ? state.expiresAt
          : now,

      cooldownUntil: now + COOLDOWN_DURATION_MS
    };

    saveStoredState(expiredState);

    return expiredState;
  }

  function normalizeState(state, cartData, now = Date.now()) {
    /*
     * Aucun état enregistré :
     * l’offre démarre uniquement si le panier est éligible.
     */
    if (!state) {
      if (cartData.isEligible) {
        return startOffer(now);
      }

      return null;
    }

    /*
     * Une période d’attente est déjà active.
     */
    if (state.cooldownUntil) {
      /*
       * La période de 7 jours est terminée.
       */
      if (now >= state.cooldownUntil) {
        clearStoredState();

        /*
         * Si le panier est déjà éligible au moment de la fin du cooldown,
         * une nouvelle offre démarre immédiatement.
         */
        if (cartData.isEligible) {
          return startOffer(now);
        }

        return null;
      }

      return state;
    }

    /*
     * Le timer vient d’expirer.
     */
    if (now >= state.expiresAt) {
      return expireOffer(state, now);
    }

    return state;
  }

  /* ==========================================================================
     Formatage
     ========================================================================== */

  function formatRemainingTime(milliseconds) {
    const totalSeconds = Math.max(
      0,
      Math.ceil(milliseconds / 1000)
    );

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(
      seconds
    ).padStart(2, '0')}`;
  }

  function formatDeadline(timestamp) {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  }

  function formatMoney(amountInCents, currency = 'EUR') {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency
    }).format(amountInCents / 100);
  }

  /* ==========================================================================
     Utilitaires d’affichage
     ========================================================================== */

  function setHidden(element, hidden) {
    if (!element) {
      return;
    }

    element.hidden = hidden;
  }

  function setText(element, text) {
    if (!element || element.textContent === text) {
      return;
    }

    element.textContent = text;
  }

  /* ==========================================================================
     Mise à jour du total natif du drawer
     ========================================================================== */

  function updateDrawerTotal(cartData, applyOffer) {
    const totalElement = document.querySelector(
      '[data-sc-drawer-total]'
    );

    const labelElement = document.querySelector(
      '[data-sc-drawer-total-label]'
    );

    if (!totalElement || !labelElement) {
      return;
    }

    const currency = totalElement.dataset.scCurrency || 'EUR';

    if (applyOffer) {
      const saving = Math.round(
        cartData.cartTotal * (cartData.percentage / 100)
      );

      const discountedTotal = Math.max(
        0,
        cartData.cartTotal - saving
      );

      setText(
        labelElement,
        'Total estimé après remise'
      );

      setText(
        totalElement,
        formatMoney(discountedTotal, currency)
      );

      totalElement.dataset.scOfferApplied = 'true';

      return;
    }

    setText(labelElement, 'Sous-total');

    setText(
      totalElement,
      formatMoney(cartData.cartTotal, currency)
    );

    delete totalElement.dataset.scOfferApplied;
  }

  /* ==========================================================================
     État 1 — Offre non déclenchée
     ========================================================================== */

  function showPreOffer(element, cartData) {
    element.hidden = false;
    element.dataset.offerState = 'available';

    setText(
      element.querySelector('[data-sc-offer-title]'),
      'Une offre t’attend avec une planche'
    );

    let message =
      'Ajoute une planche de surf à ton panier pour débloquer −5 % sur toute ta commande pendant 1 heure.';

    /*
     * Une planche est présente, mais le total du panier reste sous 300 €.
     */
    if (
      cartData.hasSurfboard &&
      cartData.cartTotal < cartData.threshold
    ) {
      const missingAmount = Math.max(
        0,
        cartData.threshold - cartData.cartTotal
      );

      message =
        `Plus que ${formatMoney(missingAmount)} pour débloquer ` +
        'ton offre de −5 %.';
    }

    /*
     * Le panier dépasse 300 €, mais il ne contient aucune planche.
     */
    if (
      !cartData.hasSurfboard &&
      cartData.cartTotal >= cartData.threshold
    ) {
      message =
        'Ajoute une planche de surf à ton panier pour débloquer ' +
        '−5 % sur toute ta commande pendant 1 heure.';
    }

    setText(
      element.querySelector('[data-sc-offer-text]'),
      message
    );

    setHidden(
      element.querySelector('[data-sc-offer-timer]'),
      true
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-progress-wrapper]'
      ),
      true
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-deadline-wrapper]'
      ),
      true
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-saving-wrapper]'
      ),
      true
    );

    updateDrawerTotal(cartData, false);
  }

  /* ==========================================================================
     État 2 et 3 — Offre active
     ========================================================================== */

  function showActiveOffer(element, state, cartData, now) {
    const remaining = Math.max(
      0,
      state.expiresAt - now
    );

    const progress = Math.max(
      0,
      Math.min(
        100,
        (remaining / OFFER_DURATION_MS) * 100
      )
    );

    element.hidden = false;

    setHidden(
      element.querySelector('[data-sc-offer-timer]'),
      false
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-progress-wrapper]'
      ),
      false
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-deadline-wrapper]'
      ),
      false
    );

    setText(
      element.querySelector('[data-sc-offer-timer]'),
      formatRemainingTime(remaining)
    );

    setText(
      element.querySelector('[data-sc-offer-deadline]'),
      formatDeadline(state.expiresAt)
    );

    const progressElement = element.querySelector(
      '[data-sc-offer-progress]'
    );

    if (progressElement) {
      progressElement.style.width = `${progress}%`;
    }

    /*
     * Le timer est actif et le panier respecte toujours les conditions.
     */
    if (cartData.isEligible) {
      element.dataset.offerState = 'active';

      setText(
        element.querySelector('[data-sc-offer-title]'),
        '−5 % sur toute ta commande'
      );

      setText(
        element.querySelector('[data-sc-offer-text]'),
        'Finalise ta commande avant la fin du compte à rebours ' +
          'pour profiter de −5 % sur toute ta commande.'
      );

      setHidden(
        element.querySelector(
          '[data-sc-offer-saving-wrapper]'
        ),
        false
      );

      updateDrawerTotal(cartData, true);

      return;
    }

    /*
     * Le timer continue, mais le panier ne remplit plus les conditions.
     */
    element.dataset.offerState = 'inactive-cart';

    setText(
      element.querySelector('[data-sc-offer-title]'),
      'Ton offre est toujours active'
    );

    let inactiveMessage =
      'Remets une planche dans ton panier et garde un total ' +
      'd’au moins 300 € avant la fin du compte à rebours pour profiter ' +
      'de ta réduction.';

    if (
      cartData.hasSurfboard &&
      cartData.cartTotal < cartData.threshold
    ) {
      const missingAmount = Math.max(
        0,
        cartData.threshold - cartData.cartTotal
      );

      inactiveMessage =
        `Ajoute encore ` +
        `${formatMoney(missingAmount)} à ton panier avant la fin ` +
        'du compte à rebours pour profiter de la réduction.';
    }

    if (
      !cartData.hasSurfboard &&
      cartData.cartTotal >= cartData.threshold
    ) {
      inactiveMessage =
        'Remets une planche de surf dans ton panier avant la fin ' +
        'du compte à rebours pour profiter de ta réduction.';
    }

    setText(
      element.querySelector('[data-sc-offer-text]'),
      inactiveMessage
    );

    setHidden(
      element.querySelector(
        '[data-sc-offer-saving-wrapper]'
      ),
      true
    );

    updateDrawerTotal(cartData, false);
  }

  /* ==========================================================================
     État 4 — Cooldown de 7 jours
     ========================================================================== */

  function showCooldown(element, state, cartData, now) {
    /*
     * Le bloc est masqué pendant les 7 jours.
     */
    element.hidden = true;
    element.dataset.offerState = 'cooldown';

    updateDrawerTotal(cartData, false);

    if (now >= state.cooldownUntil) {
      clearStoredState();
    }
  }

  /* ==========================================================================
     Mise à jour principale
     ========================================================================== */

  function updateOffer() {
    const element = getOfferElement();

    /*
     * Aucun module présent :
     * cela peut notamment arriver lorsque le panier est vide.
     */
    if (!element) {
      return;
    }

    const now = Date.now();
    const cartData = getCartData(element);

    let state = getStoredState();

    state = normalizeState(
      state,
      cartData,
      now
    );

    if (!state) {
      showPreOffer(element, cartData);
      return;
    }

    if (state.cooldownUntil) {
      showCooldown(
        element,
        state,
        cartData,
        now
      );

      return;
    }

    showActiveOffer(
      element,
      state,
      cartData,
      now
    );
  }

  function initializeOffer() {
    updateOffer();

    if (!updateInterval) {
      updateInterval = window.setInterval(
        updateOffer,
        1000
      );
    }
  }

  /* ==========================================================================
     Application de la réduction au checkout
     ========================================================================== */

  function canApplyDiscount() {
    const element = getOfferElement();

    if (!element) {
      return false;
    }

    const state = getStoredState();
    const cartData = getCartData(element);
    const now = Date.now();

    return Boolean(
      state &&
        !state.cooldownUntil &&
        state.expiresAt > now &&
        cartData.isEligible
    );
  }

  function getDiscountCheckoutUrl() {
    const element = getOfferElement();

    const code =
      element?.dataset.discountCode || 'SHAPERS5';

    return (
      `/discount/${encodeURIComponent(code)}` +
      `?redirect=${encodeURIComponent('/checkout')}`
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target.closest(
        'button[name="checkout"], a[href="/checkout"]'
      );

      if (
        !checkoutButton ||
        !canApplyDiscount()
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      window.location.assign(
        getDiscountCheckoutUrl()
      );
    },
    true
  );

  /* ==========================================================================
     Détection des rafraîchissements AJAX du panier
     ========================================================================== */

  function scheduleOfferUpdate() {
    if (observerUpdateFrame) {
      window.cancelAnimationFrame(
        observerUpdateFrame
      );
    }

    observerUpdateFrame =
      window.requestAnimationFrame(() => {
        observerUpdateFrame = null;
        updateOffer();
      });
  }

  const observer = new MutationObserver(
    (mutations) => {
      /*
       * On ignore les modifications provoquées par notre propre module
       * afin d’éviter une boucle permanente du MutationObserver.
       */
      const containsExternalMutation =
        mutations.some((mutation) => {
          const target =
            mutation.target.nodeType === Node.ELEMENT_NODE
              ? mutation.target
              : mutation.target.parentElement;

          if (!target) {
            return false;
          }

          const isInsideOffer = target.closest(
            '[data-sc-cart-flash-offer]'
          );

          const isInsideDrawerTotal = target.closest(
            '[data-sc-drawer-total], [data-sc-drawer-total-label]'
          );

          return (
            !isInsideOffer &&
            !isInsideDrawerTotal
          );
        });

      if (containsExternalMutation) {
        scheduleOfferUpdate();
      }
    }
  );

  function observeCartChanges() {
    const cartDrawer =
      document.querySelector('#CartDrawer') ||
      document.body;

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  /* ==========================================================================
     Initialisation
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeOffer();
        observeCartChanges();
      }
    );
  } else {
    initializeOffer();
    observeCartChanges();
  }
})();